// AdminRegister.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import Divider from '@mui/material/Divider';

const AdminRegister = () => {
  const [adminList, setAdminList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({ message: '', severity: '' });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });

  // Fetch all admins
  const fetchAdmins = async () => {
    try {
      const response = await axios.get('https://reviewhub-lc.onrender.com/api/admin');
      setAdminList(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

const handleRegister = async () => {
  const { username, password, confirmPassword } = formData;
  const loggedInAdmin = JSON.parse(localStorage.getItem('admin'));

  if (!username || !password || !confirmPassword) {
    setAlert({ message: 'All fields are required', severity: 'warning' });
    setOpenSnackbar(true);
    return;
  }

  if (password !== confirmPassword) {
    setAlert({ message: 'Passwords do not match', severity: 'error' });
    setOpenSnackbar(true);
    return;
  }

  try {
    const response = await axios.post('https://reviewhub-lc.onrender.com/api/admin/register', {
      username,
      password,
      createdBy: loggedInAdmin?.username || 'Unknown', // Pass current admin
    });

    setAlert({ message: response.data.message, severity: 'success' });
    setOpenSnackbar(true);
    setOpenDialog(false);
    setFormData({ username: '', password: '', confirmPassword: '' });
    fetchAdmins();
  } catch (error) {
    const msg = error.response?.data?.message || 'Registration failed';
    setAlert({ message: msg, severity: 'error' });
    setOpenSnackbar(true);
  }
};


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await axios.delete(`https://reviewhub-lc.onrender.com/api/admin/${id}`);
        setAlert({ message: 'Admin deleted successfully', severity: 'success' });
        setOpenSnackbar(true);
        fetchAdmins();
      } catch (error) {
        setAlert({ message: 'Delete failed', severity: 'error' });
        setOpenSnackbar(true);
      }
    }
  };

  return (
    <Container maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={5}>
        <Typography variant="h5">Admin Management</Typography>
        <Button variant="contained" onClick={() => setOpenDialog(true)}>
          Add Admin
        </Button>
      </Box>

      {/* Admin Registration Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add New Admin</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            margin="normal"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonAddIcon />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            margin="normal"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            margin="normal"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRegister}>Register</Button>
        </DialogActions>
      </Dialog>

      {/* Admin List Table */}
   <Box mt={5} px={2}>
  <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
    👤 Registered Admins
  </Typography>

  <Divider sx={{ my: 2 }} />

  <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Table>
      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Created By</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {adminList.map((admin) => (
          <TableRow key={admin._id} hover>
            <TableCell>{admin.username}</TableCell>
            <TableCell>{admin.createdBy || 'N/A'}</TableCell>
            <TableCell>{new Date(admin.createdAt).toLocaleString()}</TableCell>
            <TableCell>
              <Tooltip title="Delete this admin">
                <IconButton onClick={() => handleDelete(admin._id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
</Box>

      {/* Snackbar Alert */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert severity={alert.severity} onClose={() => setOpenSnackbar(false)}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminRegister;
